import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CreateCustomerRequest, CustomerDto } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import PartyStatement from './PartyStatement';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  canCreate: boolean;
  /** يُفتح كشفه مباشرة — من أعمار الديون مثلاً. */
  initialCustomerId?: string | null;
  onSessionLost: () => void;
}

const PERSON_LABELS = { B: 'منشأة', P: 'فرد' } as const;

interface Draft {
  nameAr: string;
  personType: 'B' | 'P';
  taxRegistrationNumber: string;
  phone: string;
  email: string;
  paymentTermsDays: string;
}

const EMPTY: Draft = {
  nameAr: '',
  personType: 'B',
  taxRegistrationNumber: '',
  phone: '',
  email: '',
  paymentTermsDays: '0',
};

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const paths = caught.problem.issues?.map((issue) => issue.path ?? issue.message) ?? [];
    return (
      (caught.problem.detail ?? caught.problem.title) +
      (paths.length ? ': ' + paths.join('، ') : '')
    );
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * العملاء: قائمة وإضافة. الكود يولّده الخادم، والرصيد لا يظهر هنا لأنه لا يُخزَّن
 * فى العميل — يُقرأ من كشف حسابه (يأتى مع الفواتير والتحصيل).
 */
export default function Customers({
  companyId,
  currency,
  canCreate,
  initialCustomerId = null,
  onSessionLost,
}: Props) {
  const [statementOf, setStatementOf] = useState<string | null>(initialCustomerId);
  const [customers, setCustomers] = useState<CustomerDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  // المفتاح يتجدد مع كل تعديل: ضغطتان على «حفظ» لنفس البيانات عميل واحد،
  // وبيانات مختلفة بنفس المفتاح كانت ستُرفض 409
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .customers(companyId)
      .then(setCustomers)
      .catch((caught: unknown) => {
        if (caught instanceof UnauthorizedError) onSessionLost();
        else setError(messageOf(caught));
      });
  }, [companyId, onSessionLost]);

  useEffect(load, [load]);

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setIdempotencyKey(crypto.randomUUID());
    setSaved(null);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const body: CreateCustomerRequest = {
      nameAr: draft.nameAr,
      personType: draft.personType,
      taxRegistrationNumber: draft.taxRegistrationNumber,
      phone: draft.phone,
      email: draft.email,
      paymentTermsDays: Number(draft.paymentTermsDays),
    };
    try {
      const created = await api.createCustomer(companyId, idempotencyKey, body);
      setSaved(created.code + ' — ' + created.nameAr);
      setDraft(EMPTY);
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {canCreate && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>عميل جديد</h2>
          <div className="form-row">
            <label className="grow">
              الاسم
              <input
                value={draft.nameAr}
                onChange={(event) => update('nameAr', event.target.value)}
                maxLength={200}
                required
              />
            </label>
            <label>
              النوع
              <select
                value={draft.personType}
                onChange={(event) => update('personType', event.target.value as 'B' | 'P')}
              >
                <option value="B">منشأة (فاتورة)</option>
                <option value="P">فرد (إيصال)</option>
              </select>
              <Hint>
                «منشأة» لشركة أو محل له سجل تجارى، و«شخص» لفرد. يلزم للفاتورة الإلكترونية.
              </Hint>
            </label>
            <label>
              التسجيل الضريبى
              <input
                value={draft.taxRegistrationNumber}
                onChange={(event) => update('taxRegistrationNumber', event.target.value)}
                inputMode="numeric"
                maxLength={32}
              />
              <Hint>
                رقم البطاقة الضريبية (٩ أرقام). مطلوب لو ستُصدر له فاتورة إلكترونية كشركة.
              </Hint>
            </label>
          </div>
          <div className="form-row">
            <label>
              الهاتف
              <input
                value={draft.phone}
                onChange={(event) => update('phone', event.target.value)}
                inputMode="tel"
                maxLength={32}
              />
            </label>
            <label className="grow">
              البريد
              <input
                type="email"
                value={draft.email}
                onChange={(event) => update('email', event.target.value)}
                maxLength={254}
              />
            </label>
            <label>
              مدة السداد (يوم)
              <input
                type="number"
                min={0}
                max={365}
                value={draft.paymentTermsDays}
                onChange={(event) => update('paymentTermsDays', event.target.value)}
              />
              <Hint>كم يومًا تمهل العميل للدفع بعد الفاتورة. بعدها تظهر الفاتورة «متأخرة».</Hint>
            </label>
          </div>
          {error && <p className="error">{error}</p>}
          {saved && <p className="sub">تم الحفظ: {saved}</p>}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={saving || draft.nameAr.trim().length < 2}
            >
              {saving ? 'جارى الحفظ…' : 'حفظ العميل'}
            </button>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>العملاء</h2>
        {!canCreate && error && <p className="error">{error}</p>}
        {customers === null && !error && <Loading />}
        {customers?.length === 0 && <p className="sub">لا عملاء بعد.</p>}
        {customers && customers.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الاسم</th>
                  <th>النوع</th>
                  <th>التسجيل الضريبى</th>
                  <th>الهاتف</th>
                  <th>مدة السداد</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="clickable"
                    tabIndex={0}
                    aria-selected={statementOf === customer.id}
                    onClick={() => setStatementOf(customer.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') setStatementOf(customer.id);
                    }}
                  >
                    <td className="mono">{customer.code}</td>
                    <td>{customer.nameAr}</td>
                    <td>{PERSON_LABELS[customer.personType]}</td>
                    <td className="mono">{customer.taxRegistrationNumber ?? ''}</td>
                    <td className="mono">{customer.phone ?? ''}</td>
                    <td>{customer.paymentTermsDays} يوم</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {customers && customers.length > 0 && <p className="label">اضغط أى عميل لفتح كشف حسابه.</p>}
      </div>

      {statementOf && (
        <PartyStatement
          key={statementOf}
          companyId={companyId}
          kind="customer"
          partyId={statementOf}
          currency={currency}
          onSessionLost={onSessionLost}
        />
      )}
    </>
  );
}
